import {
  DistributeQueryPayload,
  DistributeQueryResponse,
  DownloadReportPayload,
  DownloadReportResponse,
  ExecuteQueryPayload,
  ExecuteQueryResponse,
  GetAllVisualizationsResponse,
  GetReportingByIdResponse,
  GetReportTemplatesResponse,
  GetTemplateByIdPayload,
  ReportChartDataPayload,
  ReportTemplateItem,
  RunReportPayload,
  RunReportResponse,
} from 'interfaces/report-templates.interface';

import { coreSplitApis } from './core.api';

export const reportTemplates = coreSplitApis
  .enhanceEndpoints({ addTagTypes: ['Templates', 'TemplateById'] })
  .injectEndpoints({
    endpoints: (build) => ({
      getReportTemplates: build.query<GetReportTemplatesResponse, string>({
        query: (params) => ({
          url: `Reporting/GetAll?${params}`,
          method: 'GET',
        }),
        providesTags: ['Templates'],
      }),
      getById: build.query<ReportTemplateItem, number | string>({
        query: (id) => ({
          url: `Reporting/GetById?id=${id}`,
          method: 'GET',
        }),
      }),
      postDownload: build.mutation<DownloadReportResponse, DownloadReportPayload>({
        query: (payload) => ({
          url: `Reporting/Download`,
          method: 'POST',
          data: payload,
        }),
      }),
      deleteTemplate: build.mutation<void, number>({
        query: (templateId) => ({
          url: `Reporting/Delete?id=${templateId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, error) => (error ? [] : ['Templates']),
      }),
      getAllVisualizations: build.query<GetAllVisualizationsResponse, void>({
        query: () => ({
          url: `ReportVisualizationType/GetAll`,
          method: 'GET',
        }),
      }),
      postExecuteQuery: build.mutation<ExecuteQueryResponse, ExecuteQueryPayload>({
        query: (payload) => ({
          url: 'Reporting/ExecuteQuery',
          method: 'POST',
          data: payload,
        }),
      }),
      distribute: build.mutation<DistributeQueryResponse, DistributeQueryPayload>({
        query: (payload) => ({
          url: 'Reporting/Distribute',
          method: 'POST',
          data: payload,
        }),
      }),
      getReportingById: build.query<GetReportingByIdResponse, string>({
        query: (reportId) => ({
          url: `Reporting/GetById?id=${reportId}`,
          method: 'GET',
        }),
      }),
      postChartData: build.mutation<DistributeQueryResponse, ReportChartDataPayload>({
        query: (payload) => ({
          url: 'Reporting/GetChartData',
          method: 'POST',
          data: payload,
        }),
      }),
      runReport: build.mutation<RunReportResponse, RunReportPayload>({
        query: (payload) => ({
          url: `Reporting/Run`,
          method: 'POST',
          data: payload,
        }),
      }),
      postCreateTemplate: build.mutation<void, Record<string, unknown>>({
        query: (payload) => ({
          url: 'Reporting/CreateTemplate',
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: (_, error) => (error ? [] : ['Templates', 'TemplateById']),
      }),
      getTemplateById: build.query<GetTemplateByIdPayload, number | string>({
        query: (id) => ({
          url: `Reporting/GetTemplate?id=${id}`,
          method: 'GET',
        }),
        providesTags: (_, __, id) => [{ type: 'TemplateById', id }],
      }),
      postEditTemplate: build.mutation<void, Record<string, unknown>>({
        query: (payload) => ({
          url: 'Reporting/EditTemplate',
          method: 'POST',
          data: payload,
        }),
        invalidatesTags: (_, error) => (error ? [] : ['Templates', 'TemplateById']),
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetReportTemplatesQuery,
  useGetByIdQuery,
  usePostDownloadMutation,
  useDeleteTemplateMutation,
  useGetAllVisualizationsQuery,
  usePostExecuteQueryMutation,
  useDistributeMutation,
  useGetReportingByIdQuery,
  usePostChartDataMutation,
  useRunReportMutation,
  usePostCreateTemplateMutation,
  useGetTemplateByIdQuery,
  usePostEditTemplateMutation,
} = reportTemplates;
